@use "@design/design-tokens/typography/typography" as *;
@use "@design/design-tokens/border-radius/border-radius" as *;
@use "@design/components/custom-scroll/custom-scroll" as *;

.dropdownContent {
  display: flex;
  flex-direction: column;
  min-width: 290px;
  max-width: 400px;
  max-height: min(
    400px,
    var(--radix-dropdown-menu-content-available-height, 400px)
  );
  overflow-y: auto;
  background-color: var(--surface-default);
  border: 1px solid var(--border-soft);
  @include custom-scroll-minimal;

  box-shadow: 0 6px 10px 4px rgba(0, 0, 0, 15%), 0 2px 3px 0 rgba(0, 0, 0, 30%);
  z-index: var(--z-index-modal);

  @media (max-height: 700px) {
    max-height: min(
      360px,
      var(--radix-dropdown-menu-content-available-height, 360px)
    );
  }
}

.dropdownMenuContent {
  composes: dropdownContent;
}

.includeExcludeFilterContent {
  @include border-radius-r2;

  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  overflow: hidden;
  border: 1px solid var(--border-soft);
  background: var(--surface-default);
  box-shadow:
    0 2px 5px -4px rgba(0, 0, 0, 50%),
    0 5px 10px 0 rgba(0, 0, 0, 50%);

  > * {
    width: 100%;
  }

  > .scrollableList {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
  }
}

.subMenuContent {
  composes: dropdownContent;
  scrollbar-gutter: stable;
}

.menuItemLeftIndicator {
  display: inline-block;
  width: 4px;
  height: auto;
  color: transparent;
  border-left: solid 3px;
  border-top-right-radius: 80px 80px;
  border-bottom-right-radius: 80px 80px;
}

.itemText {
  @include border-radius-r1;

  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin: 0 4px;
  padding: 8px;
  width: 100%;
  cursor: pointer;
}

.itemTextRow {
  composes: itemText;
  flex-direction: row;
  align-items: center;
  gap: 8px;
}

.resetButton {
  @include border-radius-r1;

  display: flex;
  align-items: center;
  margin: 0 8px;
  padding: 8px;
  width: 100%;
  cursor: pointer;
}

.resetIconWrapper {
  display: inline-flex;
  margin-top: auto;
}

.resetIcon {
  margin-right: 8px;
}

.resetButtonLabel {
  @include typography-v2-body-sm;

  color: var(--text-primary);
}

.resetItem {
  all: unset;
  display: flex;
  padding: 8px;

  &[data-highlighted] {
    [data-reset-button] {
      background-color: var(--surface-hover);
    }
  }
}

.lockedIcon {
  fill: var(--icon-disabled);
}

.itemLabel {
  @include typography-v2-body-sm;

  color: var(--text-default);
}

.itemLabelLocked {
  composes: itemLabel;
  color: var(--text-disabled);
}

.customLabel {
  @include typography-v2-body-sm;

  display: flex;
  align-items: center;
  gap: 4px;
}

.removeLabel {
  composes: customLabel;
  color: var(--text-critical);

  svg {
    fill: currentColor;
  }
}

.itemDescription {
  @include typography-v2-body-xs;

  display: block;
  color: var(--text-soft);
  padding-left: var(--indent, 0);
}

.countBadge {
  @include border-radius-full;

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  background: var(--surface-primary);
  color: var(--text-on-primary);
}

.checkboxItem {
  all: unset;
  display: flex;
  margin-bottom: 4px;

  [data-checkmark] {
    margin-top: 0;
  }

  [data-checkbox] {
    align-items: center;
  }

  &[data-highlighted] {
    [data-item-text] {
      background-color: var(--surface-hover);
    }
  }

  &[data-state="checked"] {
    [data-item-text] {
      background-color: var(--surface-active);
    }
  }

  &[data-disabled] {
    filter: contrast(40%);
  }
}

.item {
  all: unset;
  display: flex;
  margin-bottom: 4px;

  &[data-highlighted] {
    [data-item-text] {
      background-color: var(--surface-hover);
    }
  }

  &[data-disabled] {
    [data-item-text] {
      cursor: default;
    }
  }
}

.itemChecked {
  [data-menu-item-indicator] {
    color: var(--surface-primary);
    border-color: var(--surface-primary);
  }

  [data-item-text] {
    background-color: var(--surface-active);
  }
}

.dropdownMenuTitle {
  display: flex;
  padding: 0 16px;
}

.dropdownMenuTitleText {
  @include typography-v2-body-xs;

  color: #9ba1a6;
  flex: 1;
  padding: 8px 0;
  align-items: flex-start;
  text-transform: uppercase;
}

.menuTitleText {
  composes: dropdownMenuTitleText;
  padding: 8px 16px;
}

.triggerLabel {
  @include typography-v2-body-sm;
}

.separator {
  height: 1px;
  background-color: var(--border-soft);
  margin-bottom: 8px;
}

.includeExcludeFilterMode {
  display: flex;
  padding: 0 8px 8px;

  [data-button-group] {
    @include border-radius-r2;

    display: flex;
    height: 32px;
    padding: 4px;
    align-items: flex-start;
    gap: 4px;
    flex: 1 0 0;
    border: 1px solid var(--border-soft);
    background: var(--background-soft);
  }

  [data-button-group-item] {
    height: 100%;
    padding: 0 8px;
  }
}

.includeExcludeFooterActions {
  padding: 4px 8px 8px;

  .resetItem {
    padding: 0;
  }

  .resetButton {
    margin: 0;
    padding: 8px;
  }
}

.includeExcludeFooter {
  position: sticky;
  z-index: 1;
  bottom: 0;
  flex: 0 0 auto;
  background: var(--surface-default);

  &::before {
    position: absolute;
    right: 0;
    bottom: 100%;
    left: 0;
    height: 24px;
    background: linear-gradient(to bottom, transparent, var(--surface-default));
    content: "";
    pointer-events: none;
  }
}

.trigger {
  @include border-radius-r2;

  display: inline-flex;
  background-color: var(--surface-soft);
  height: 32px;
  padding: 6px 8px 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  border: 1px solid var(--border-default);
  cursor: pointer;

  [data-trigger-label] {
    color: var(--text-soft);
  }

  &:hover {
    background: var(--surface-hover);

    [data-trigger-label] {
      color: var(--text-default);
    }
  }

  &[data-state="open"] {
    background-color: var(--surface-active);

    [data-trigger-label] {
      color: var(--text-default);
    }
  }

  &[data-disabled] {
    background-color: var(--surface-soft);
    cursor: default;

    [data-trigger-label] {
      color: var(--text-disabled);
    }
  }
}

.triggerActive {
  composes: trigger;
  background-color: var(--surface-active);
  border-color: var(--surface-active);

  [data-trigger-label] {
    color: var(--text-default);
  }
}

.dashedTrigger {
  composes: trigger;
  border: 1px dashed var(--border-hover);
}

.selectedValue {
  @include typography-v2-body-sm;

  display: inline-flex;
  gap: 4px;
  color: var(--text-primary);
  align-items: center;
}

.subTriggerLabel {
  font-weight: 500;

  &::first-letter {
    text-transform: uppercase;
  }
}

.subTrigger {
  @include typography-v2-body-sm;
  @include border-radius-r2;

  display: inline-flex;
  background-color: var(--surface-soft);
  height: 32px;
  padding: 6px 8px 6px 12px;
  justify-content: center;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
  color: var(--text-primary);
  outline: none;

  &:hover {
    background: var(--surface-hover);
  }

  &[data-state="open"] {
    background-color: var(--surface-soft);
  }

  &[data-disabled] {
    background-color: var(--surface-soft);
  }

  &::first-letter {
    text-transform: uppercase;
  }
}

.scrollableList {
  @include custom-scroll-minimal;

  scrollbar-gutter: stable;
  overflow-y: auto;
}

.noResults {
  @include typography-v2-body-sm;

  padding: 8px;
  margin: 8px;
}

.customHeaderLabel {
  @include typography-v2-body-xs;

  color: var(--text-soft);
  padding: 10px 16px 2px;
}


.customBottomBorder {
  height: 1px;
  background-color: var(--border-soft);
  margin-top: 8px;
}
